import { TaxGroup } from "@/types/system-setting/tax-group";
import { SaleTax } from "@/types/system-setting/sale-tax";

/**
 * Percentage-based tax selection
 * Intersection: same percentage exists in BOTH groups
 * Priority: Highest percentage > is_srb: true > any
 */
export const calculateTaxFromTaxGroups = (
  productTaxGroup: TaxGroup | null | undefined,
  memberTaxGroup: TaxGroup | null | undefined
): { tax: SaleTax | null; taxRate: number; taxName: string } => {
  if (!productTaxGroup?.taxes?.length || !memberTaxGroup?.taxes?.length) {
    console.log("🧾 Tax Calculation:", {
      productTaxGroup: productTaxGroup
        ? {
            id: productTaxGroup.id,
            name: productTaxGroup.name,
            taxes: productTaxGroup.taxes ?? [],
          }
        : null,
      memberTaxGroup: memberTaxGroup
        ? {
            id: memberTaxGroup.id,
            name: memberTaxGroup.name,
            taxes: memberTaxGroup.taxes ?? [],
          }
        : null,
      intersectionTaxes: [],
      selectedTax: null,
      selectionReason: "Missing product or member tax group",
      rules:
        "Intersection: same percentage in BOTH groups. Priority: Highest percentage > is_srb: true > any",
    });

    return { tax: null, taxRate: 0, taxName: "" };
  }

  // Build a set of member percentages
  const memberPercentages = new Set(
    memberTaxGroup.taxes.map(tax => tax.percentage)
  );

  // Intersection by percentage
  const intersectionTaxes: SaleTax[] = productTaxGroup.taxes.filter(tax =>
    memberPercentages.has(tax.percentage)
  );

  if (intersectionTaxes.length === 0) {
    console.log("🧾 Tax Calculation:", {
      productTaxGroup: {
        id: productTaxGroup.id,
        name: productTaxGroup.name,
        taxes: productTaxGroup.taxes,
      },
      memberTaxGroup: {
        id: memberTaxGroup.id,
        name: memberTaxGroup.name,
        taxes: memberTaxGroup.taxes,
      },
      intersectionTaxes: [],
      selectedTax: null,
      selectionReason:
        "No intersection - no matching percentages between groups",
      rules:
        "Intersection: same percentage in BOTH groups. Priority: Highest percentage > is_srb: true > any",
    });

    return { tax: null, taxRate: 0, taxName: "" };
  }

  let selectionReason = "Initial selection (first matching percentage)";

  const selectedTax = intersectionTaxes.reduce((best, current) => {
    if (current.percentage > best.percentage) {
      selectionReason = `Higher percentage (${current.percentage}% > ${best.percentage}%)`;
      return current;
    }

    if (
      current.percentage === best.percentage &&
      current.is_srb &&
      !best.is_srb
    ) {
      selectionReason = `Same percentage (${current.percentage}%) but is_srb: true`;
      return current;
    }

    return best;
  });

  console.log("🧾 Tax Calculation:", {
    productTaxGroup: {
      id: productTaxGroup.id,
      name: productTaxGroup.name,
      taxes: productTaxGroup.taxes.map(t => ({
        id: t.id,
        name: t.name,
        percentage: t.percentage,
        is_srb: t.is_srb,
      })),
    },
    memberTaxGroup: {
      id: memberTaxGroup.id,
      name: memberTaxGroup.name,
      taxes: memberTaxGroup.taxes.map(t => ({
        id: t.id,
        name: t.name,
        percentage: t.percentage,
        is_srb: t.is_srb,
      })),
    },
    intersectionTaxes: intersectionTaxes.map(t => ({
      id: t.id,
      name: t.name,
      percentage: t.percentage,
      is_srb: t.is_srb,
    })),
    selectedTax: {
      id: selectedTax.id,
      name: selectedTax.name,
      percentage: selectedTax.percentage,
      is_srb: selectedTax.is_srb,
    },
    selectionReason,
    rules:
      "Intersection: same percentage in BOTH groups. Priority: Highest percentage > is_srb: true > any",
  });

  return {
    tax: selectedTax,
    taxRate: selectedTax.percentage,
    taxName: selectedTax.name,
  };
};